// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Clickable from "@pace/MobiusOverrides/Clickable/Clickable";
import Tooltip from "@pace/MobiusOverrides/Tooltip";
import IconHelp from "@pace/XDL/Icons/IconHelp";
import React, { ComponentType } from "react";

export interface ProductTooltipProps {
    label?: string;
    tooltipText?: string;
    extendedStyles?: ProductTooltipStyles;
    icon?: string | ComponentType<{}> | undefined;
    onClick?: () => void;
    disabled?: boolean;
    value?: string;
}

export type ProductTooltipStyles = {
    wrapper?: InjectableCss;
    trigger?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
};

export const productTooltipStyles: ProductTooltipStyles = {};

const ProductTooltip = ({
    tooltipText,
    label,
    extendedStyles,
    icon = IconHelp,
    disabled,
    onClick,
    value,
}: ProductTooltipProps) => {
    const styles = React.useMemo(() => mergeToNew(productTooltipStyles, extendedStyles), [extendedStyles]);
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="ProductTooltipWrapper">
            <Clickable
                className="info-trigger-container"
                data-test-selector="InfoTriggerWrapper"
                {...styles.trigger}
                onClick={onClick}
                aria-disabled={disabled}
                disabled={disabled}
            >
                <Typography data-test-selector="InfoTrigger" {...styles.label}>{label || translate("Important Info")}</Typography>
                {value &&
                    <Typography data-test-selector="InfoTriggerValue" {...styles.value}>{value}</Typography>
                }
            </Clickable>
            <Tooltip
                text={tooltipText}
                triggerComponent={<Icon src={icon} width={13} height={13} />}
                offset={[0, 16]}
                onClick={onClick}
            />
        </StyledWrapper>
    );
};

export default ProductTooltip;
